import React from 'react';
import PropTypes from 'prop-types';
import TeamColourDot from 'components/admin/TeamColourDot';
import { formatPoints } from 'utils/points';
import { formatUpdatedAt } from 'utils/meetPoints';
import styles from './HouseTotals.module.css';

// The House Totals card at the top of the Meet Points page, for a school with houses
// (docs/school-carnivals-points-slice-4a.md in the portal repo). The rows are the server's
// (MeetPointsDashboardDto.houses), best first: each house's place (houses on equal totals share one,
// by the meet's ties rule; none for a house with a total of 0, listed last, shown as –), its students'
// points, its relay points, the total, the students who took part in an event, and the total divided by
// them (the column reads Athletes: "We use Athletes in this portal not students", Doug, 2026-10-26). The
// columns are the settings mock-up's, its Typed in column named Bonus (slice 4c: the house's
// bonus points, from the Bonus Points card below; negative for a deduction); the place is a badge, as in
// the athletes table below. A house with a total of 0 or below has no place (slice 4c). When the page was worked out, and the live badge, sit at the
// top right of the card (Doug, 2026-10-24, H9).

const placeBadgeClass = (place) => {
  if (place === 1) return styles.placeGold;
  if (place === 2) return styles.placeSilver;
  if (place === 3) return styles.placeBronze;
  return styles.placeDefault;
};

const HouseTotals = ({ houses, teamWords, calculatedAt, liveStatus }) => {
  if (!houses || houses.length === 0) return null;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>{teamWords.team} Totals</h3>
        <div className={styles.lastUpdated}>
          Last updated: {formatUpdatedAt(calculatedAt)}
          {liveStatus === 'live' && <span className={styles.statusBadgeLive}>Live updates</span>}
        </div>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.centered}>Place</th>
              <th>{teamWords.team}</th>
              <th className={styles.number}>Individual Events</th>
              <th className={styles.number}>Relays</th>
              <th className={styles.number}>Bonus</th>
              <th className={styles.number}>Total</th>
              <th className={styles.number}>Athletes</th>
              <th className={styles.number}>Average</th>
            </tr>
          </thead>
          <tbody>
            {houses.map((house) => (
              <tr key={house.teamId}>
                <td className={styles.centered}>
                  {/* A house with a total of 0 has no place (Doug, 2026-10-25, H18). */}
                  {house.place ? (
                    <span className={`${styles.placeBadge} ${placeBadgeClass(house.place)}`}>{house.place}</span>
                  ) : (
                    '–'
                  )}
                </td>
                <td className={styles.name}>
                  <span className={styles.nameInner}>
                    <TeamColourDot colour={house.colour} />
                    {house.name}
                  </span>
                </td>
                <td className={styles.number}>{formatPoints(house.individualPoints)}</td>
                <td className={styles.number}>{formatPoints(house.relayPoints)}</td>
                <td className={styles.number}>{formatPoints(house.bonusPoints ?? 0)}</td>
                <td className={`${styles.number} ${styles.total}`}>{formatPoints(house.totalPoints)}</td>
                <td className={styles.number}>{house.students}</td>
                <td className={styles.number}>
                  {house.averagePoints === null || house.averagePoints === undefined
                    ? '–'
                    : formatPoints(house.averagePoints)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

HouseTotals.propTypes = {
  houses: PropTypes.arrayOf(
    PropTypes.shape({
      teamId: PropTypes.string,
      name: PropTypes.string,
      colour: PropTypes.string,
      place: PropTypes.number,
      individualPoints: PropTypes.number,
      relayPoints: PropTypes.number,
      bonusPoints: PropTypes.number,
      totalPoints: PropTypes.number,
      students: PropTypes.number,
      averagePoints: PropTypes.number,
    })
  ),
  // The organisation's words for a house (utils/teamLabel.js).
  teamWords: PropTypes.shape({ team: PropTypes.string }).isRequired,
  // When the page was worked out (MeetPointsDashboardDto.calculatedAt), and whether it updates live.
  calculatedAt: PropTypes.string,
  liveStatus: PropTypes.oneOf(['live']),
};

export default HouseTotals;
